import 'apps/hiring/styles/components/applicant/target-data.scss';
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { set, resolve } from 'library/helpers/path-resolver';
import HiringQuery from 'apps/hiring/util/query';
import withData from 'util/hoc/with-data';
import Collapsable from 'library/components/collapsable';
import Loading from 'components/loading';
import CaretSvg from 'library/icons/caret.svg';
import 'library/styles/global/rotate.scss';

import TargetDataHeader from 'apps/hiring/components/applicant/target-data-header';
import TargetContent from 'apps/hiring/components/applicant/target-content';
import TargetSlice from 'apps/hiring/components/applicant/target-slice';

class TargetData extends React.Component {
  static propTypes = {
    graph_data: PropTypes.object.isRequired,
    graph_loading: PropTypes.bool,
    id: PropTypes.number,
    user: PropTypes.object
  }

  state = {
    focus: null,
    active: null,
    sort: '',
    sorts: {
      attributes: null,
      attributeScore: null,
      attributeMatch: null
    }
  }

  toggleActive = (e) => {
    const id = e.currentTarget.dataset.id;
    this.setState({
      active: this.state.active===id?null:id,
      focus: null
    });
  }

  setFocus = (t) => {
    this.setState({
      focus: this.state.focus===t?null:t
    });
  }

  getRange = (score) => {
    if (score <= 25) {
      return 'low';
    } else if (score > 25 && score < 75) {
      return 'medium';
    }
    return 'high';
  }

  closeDrawer = () => {
    this.setState({
      active: false
    });
  }

  sortIt = (column) => {
    let [key='',dir=''] = this.state.sort.split(':');
    if (column == key) {
      if (dir == 'up') {
        dir = 'down';
      } else {
        dir = 'up';
      }
    } else {
      dir = 'up';
    }
    this.setState({ sort: `${column}:${dir}`});
  }

  compareValueMatch = (a,b, dir) => {
    const am = Number(resolve(a, 'characteristic/score', 0)) > a.percentile_range.end
      ? Number(resolve(a, 'characteristic/score', 0)) - a.percentile_range.end
      : Number(resolve(a, 'characteristic/score', 0)) - a.percentile_range.start;

    const bm = Number(resolve(b, 'characteristic/score', 0)) > b.percentile_range.end
      ? Number(resolve(b, 'characteristic/score', 0)) - b.percentile_range.end
      : Number(resolve(b, 'characteristic/score', 0)) - b.percentile_range.start;

    let comparison = 0;
    if (am > bm) {
      comparison = 1;
    } else if (am < bm) {
      comparison = -1;
    }
    return dir == 'down' ?
      (comparison * -1) : comparison;
  }

  sortRouter = (a, b) => {
    if (!this.state.sort) return 0;
    const [key='', dir=''] = this.state.sort.split(':');
    const direction = dir==='down'?-1:1;

    if (key === 'match') return this.compareValueMatch(a,b, dir);

    let ret = 0;
    if (isNaN(resolve(a, key)) || isNaN(resolve(b, key))) {
      ret = `${resolve(a, key)}`.localeCompare(`${resolve(b, key)}`);
    } else {
      ret = resolve(a, key) - resolve(b, key);
    }

    return ret * direction;
  }

  mapTargets = ({ id, percentile_range={}, characteristic={} }) => {
    const range = this.state.active==id
      ? this.state.focus||this.getRange(Number(characteristic.score))
      : 'medium';

    return (
      <Fragment key={id}>
        <TargetSlice
          id={id}
          percentile_range={percentile_range}
          characteristic={characteristic}
          range={range}
          active={this.state.active==id}
          toggleActive={this.toggleActive}
        />
        <Collapsable component="div" className="hiring-application-target-content" collapsed={this.state.active!=id}>
          <div className="hiring-application-target-content-wrapper">
            <a onClick={this.closeDrawer} href="javascript:void(0)">×</a>
            <TargetContent
              setFocus={this.setFocus}
              focus={range}
              score={Number(resolve(characteristic, 'score', 0))}
              description={resolve(characteristic, 'description', '')}
              percentile_range={percentile_range}
              content={resolve(characteristic, 'content', {})}
            />
          </div>
        </Collapsable>
      </Fragment>
    );
  }

  render() {
    const {
      graph_data,
      graph_loading,
      id,
    } = this.props;

    const {
      active,
      sort
    } = this.state;

    if (graph_loading) {
      return <Loading />;
    }

    const [key, dir] = sort.split(':');

    return (
      <Fragment>
        <TargetDataHeader
          closed={resolve(graph_data, 'closed', false)}
          openingId={resolve(graph_data, 'opening/id', 0)}
          targetId={id}
          user={resolve(graph_data, 'user', {})}
          {...resolve(graph_data, 'target', {})}
        />
        <div className="hiring-applicant-target-data-header">
          <span>Scores</span>
          <span onClick={() => this.sortIt('characteristic/name')}>Attributes <CaretSvg className={['flip-then-rotate-180', dir == 'up' && key == 'characteristic/name' ? null: 'active'].filter(c=>c).join(' ')}/> </span>
          <span onClick={() => this.sortIt('characteristic/score')}>Attribute Score <CaretSvg className={['flip-then-rotate-180', dir == 'up' && key == 'characteristic/score' ? null: 'active'].filter(c=>c).join(' ')}/> </span>
          <span onClick={() => this.sortIt('match')}>Attribute Match <CaretSvg className={['flip-then-rotate-180', dir == 'up' && key == 'match' ? null: 'active'].filter(c=>c).join(' ')}/> </span>
        </div>
        <div className={['hiring-application-target-data', active?'active':null].filter(c=>c).join(' ')}>
          {
            resolve(graph_data, 'target/targets', []).sort(this.sortRouter).map(this.mapTargets)
          }
        </div>
      </Fragment>
    );
  }
}

TargetData.parseQueryData = (data) => {
  const out = {
    target: resolve(data, 'application/opening/target_grouping', {}),
    user: resolve(data, 'application/user', {}),
    opening: resolve(data, 'application/opening', {})
  };

  out.target.targets.map((t)=>{
    const questions = {};

    for (const question of resolve(t, 'characteristic/content/questions', [])) {
      if (!questions[question.development]) {
        questions[question.development] = {
          development: question.development,
          content_range: question.content_range,
          questions: []
        };
      }

      questions[question.development].questions.push(question.question);
    }

    set(t, 'characteristic/content/questions', [
      questions.low,
      questions.medium,
      questions.high
    ]);

    return t;
  });

  return out;
};

TargetData.query = (CancelToken, { applicantId, id }) => (
  HiringQuery(`
    {
      application (id:${applicantId}){
        user {
          id
        },
        opening {
          id,
          closed,
          target_grouping (target_id:${id}){
            id,
            name,
            match,
            description,
            targets {
              id
              characteristic {
                name
                description,
                score,
                content {
                  insights {
                    development
                    content_range {
                      start,
                      end
                    },
                    id,
                    insight
                  }
                  tips {
                  	development,
                    content_range {
                      start,
                      end
                    }
                    id,
                    insight
                  }
                  questions {
                    content_range {
                      start,
                      end
                    }
                    development,
                    question
                  }
                }
              }
              percentile_range {
                start,
                end
              }
            }
    			}
        }
      }
    }
  `)
);

export default withData(TargetData);
